import { BitfStrapiModel } from '@common/libs/bitforce/core/models/strapi/bitf-strapi.model';
import { Garage } from './garage.model';
import { InvestmentDuration } from './investment-duration.model';
import { Image } from './image.model';
import { Valuation } from './valuation.model';
import { EInvestmentLength } from '@common/enums';

export class VehicleValuation extends BitfStrapiModel {
  // Relations
  garage: Garage;
  investmentDuration: InvestmentDuration;
  suggestedInvestmentDuration: InvestmentDuration;
  guaranteedFutureSellPricePercentage = 0.3;

  calculatorSettings = {
    avoidInsurance: false,
    haveOtherCarsInsured: false,
    useGarageService: false,
    guaranteedFutureValue: false,
  };

  get valuation(): Valuation {
    return this.garage?.valuations.find(
      valuation => valuation.investmentDuration.length === this.investmentDuration?.length
    );
  }

  get investmentDurationInYears(): number {
    switch (this.investmentDuration?.length) {
      case EInvestmentLength.MONTHS_6:
        return 0.5;
      case EInvestmentLength.YEARS_1:
        return 1;
      case EInvestmentLength.YEARS_2:
        return 2;
      case EInvestmentLength.YEARS_3:
        return 3;
    }
  }

  // Passaggio di proprietà
  get changeOfOwnershipPrice(): number {
    // Se ha 30 anni o e' asi? boh paga 250 fisse
    if (this.garage?.is30YearsOld) {
      return 250;
    }
    // TODO stima con i CV
    return this.garage?.changeOfOwnershipPrice;
  }

  // Bollo
  get carTaxPricePerYear(): number {
    // Se ha 30 anni paga 29 altrimenti lo stimato
    if (this.garage?.is30YearsOld) {
      return 29;
    } else if (this.garage?.isAsi || this.garage?.isAsiGold) {
      return this.garage?.carTaxPrice / 2;
    }
    return this.garage?.carTaxPrice;
  }
  get carTaxPrice(): number {
    return this.carTaxPricePerYear * this.investmentDurationInYears;
  }

  // Assicurazione
  get insurancePricePerYear(): number {
    if (this.calculatorSettings.avoidInsurance) {
      return 0;
    }

    // Se ha 30 anni o e' asi paga Sara assicurazioni 190 o 50% se piu' macchine
    if (this.garage.is30YearsOld || this.garage.isAsi || this.garage.isAsiGold) {
      return this.calculatorSettings.haveOtherCarsInsured ? 90 : 190;
    }
    return this.garage.insurancePrice;
  }
  get insurancePrice(): number {
    return this.insurancePricePerYear * Math.ceil(this.investmentDurationInYears);
  }

  // Spese di manutenizione
  get maintenancePricePerYear(): number {
    return this.garage.maintenancePrice;
  }
  get maintenancePrice(): number {
    return this.maintenancePricePerYear * Math.ceil(this.investmentDurationInYears);
  }

  // Garage
  get garagePricePerYear(): number {
    if (this.calculatorSettings.useGarageService) {
      return this.garage.garagePrice;
    }
    return 0;
  }
  get garagePrice(): number {
    return this.garagePricePerYear * this.investmentDurationInYears;
  }

  get buyPrice() {
    return this.garage?.buyPrice;
  }
  get sellPrice() {
    if (this.calculatorSettings.guaranteedFutureValue) {
      return this.guaranteedFutureSellPrice;
    }
    return this.valuation?.sellPrice;
  }

  get guaranteedFutureSellPrice() {
    return (
      this.buyPrice + (this.valuation?.sellPrice - this.buyPrice) * this.guaranteedFutureSellPricePercentage
    );
  }

  get gainInCash() {
    return this.sellPrice - this.buyPrice;
  }

  get gainInPercentage() {
    return ((this.gainInCash / this.buyPrice) * 100).toFixed(1);
  }

  get netGainInCash() {
    return (
      (this.sellPrice || 0) -
      (this.buyPrice || 0) -
      (this.changeOfOwnershipPrice || 0) -
      (this.carTaxPrice || 0) -
      (this.insurancePrice || 0) -
      (this.maintenancePrice || 0) -
      (this.garagePrice || 0)
    );
  }

  get netGainInPercentage() {
    return Number(((this.netGainInCash / this.buyPrice) * 100).toFixed(1));
  }

  constructor(data?: any) {
    super({
      data,
      hydrateProperties: {
        garage: { model: Garage },
        investmentDuration: { model: InvestmentDuration },
      },
      serializeProperties: [],
    });
    this.suggestedInvestmentDuration = new InvestmentDuration(this.investmentDuration);
  }
}
